import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useDashboardStore } from '@/stores/dashboardStore'
import { useDataFetcher } from '@/hooks/useApiManager'
import { apiService } from '@/services/api'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import UserAvatar from '@/components/UserAvatar'
import { RefreshCw, CheckCircle2, XCircle } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { DatePicker } from '@/components/ui/date-picker'

const RECENT_CLIENT_COUNT = 6

const pct = (consumed, total) => (total > 0 ? Math.min(100, (consumed / total) * 100) : 0)
const stat = (value, unit) => (Number(value) > 0 ? `${value}${unit}` : '—')
const linkClass = 'text-[13px] font-medium text-primary transition-colors hover:text-primary/80'

/** Bordered card with a header row (title left, optional link right) — the dashboard's one container. */
const Panel = ({ title, action, children, className = '' }) => (
  <Card className={`overflow-hidden ${className}`}>
    <div className="flex min-h-12 items-center justify-between gap-3 border-b border-slate-200 px-4 py-2.5">
      <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
      {action}
    </div>
    {children}
  </Card>
)

const PageTitle = ({ title, subtitle, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
    <div className="min-w-0">
      <h1 className="truncate text-[1.625rem] font-semibold leading-tight tracking-[-0.025em] text-slate-900">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{children}</div>}
  </div>
)

const Dashboard = () => {
  const navigate = useNavigate()
  const { user, companyUrl, companyDetails } = useAuthStore()
  const { dashboardData, isLoading, error, fetchDashboardData } = useDashboardStore()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [recentClients, setRecentClients] = useState({ rows: [], count: 0 })
  const isConsultantUser = user?.role?.name === 'Consultant'
  const isSuperAdmin = user?.role?.id === 1
  const isCompanyAdmin = user?.role?.name === 'Company Admin'
  const firstName = (user?.firstname || user?.name || user?.firstName || user?.username || '').split(' ')[0]

  useDataFetcher(
    async () => fetchDashboardData(companyUrl, selectedDate),
    [companyUrl, selectedDate],
    {
      enabled: !!companyUrl && !isConsultantUser && !isSuperAdmin && !isCompanyAdmin,
      requestKey: `page:fetchDashboardData:${companyUrl}:${selectedDate?.toISOString()}`
    }
  )

  // Consultant roster: the same /consultantclients call the Clients page makes, newest first
  useEffect(() => {
    if (!isConsultantUser || !companyUrl) return undefined
    const controller = new AbortController()
    apiService
      .post(`/${companyUrl}/consultantclients`, { fromIndex: 0, limit: RECENT_CLIENT_COUNT, orderBy: 'DESC', orderByOn: 'id', searchtext: '' }, controller.signal)
      .then((result) => {
        if (!result?.success) return
        const node = result.data?.consultantclients || result.data
        setRecentClients({ rows: node?.rows || [], count: node?.count || 0 })
      })
      .catch(() => {})
    return () => controller.abort()
  }, [isConsultantUser, companyUrl])

  const handleRefresh = () => {
    if (companyUrl && !isConsultantUser && !isSuperAdmin && !isCompanyAdmin) {
      fetchDashboardData(companyUrl, selectedDate)
    }
  }

  if (isConsultantUser) {
    const consultantDashboardImage = companyDetails?.dashboard_image
    const clientName = (client) =>
      [client.firstname, client.middlename, client.lastname].filter(Boolean).join(' ') || client.email || 'Unnamed client'

    return (
      <div className="dashboard-content space-y-5">
        <PageTitle
          title={`Welcome back${firstName ? `, ${firstName}` : ''}`}
          subtitle={recentClients.count > 0 ? `${recentClients.count.toLocaleString()} clients on your roster` : undefined}
        >
          <Button onClick={() => navigate('/consultant-clients')}>Add a client</Button>
        </PageTitle>

        <Panel title="Recently added clients" action={<Link to="/consultant-clients" className={linkClass}>View all clients</Link>}>
          {recentClients.rows.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500">No clients yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead className="border-b border-slate-200 bg-background">
                  <tr>
                    {['Client', 'Consultant', 'Phone', 'Location', 'Date of birth'].map((label) => (
                      <th key={label} className="h-10 whitespace-nowrap px-4 text-xs font-semibold text-slate-500">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentClients.rows.map((client) => {
                    const name = clientName(client)
                    const consultant = client.consultant
                      ? [client.consultant.firstname, client.consultant.lastname].filter(Boolean).join(' ')
                      : 'Unassigned'
                    return (
                      <tr
                        key={client.id}
                        onClick={() => navigate(`/consultant-clients/${client.id}`)}
                        className="cursor-pointer border-b border-slate-100 transition-colors last:border-b-0 hover:bg-background"
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-3">
                            <UserAvatar name={name} email={client.email} className="h-[30px] w-[30px] flex-shrink-0" />
                            <div className="min-w-0">
                              <Link
                                to={`/consultant-clients/${client.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="block truncate text-sm font-semibold text-slate-900 hover:text-primary"
                              >
                                {name}
                              </Link>
                              <div className="max-w-[240px] truncate text-xs text-slate-500">{client.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-sm text-slate-900">{consultant}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-sm text-slate-900">
                          {client.personal?.mobilephone || client.mobilephone || client.mobile || 'Not set'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-sm text-slate-900">
                          {[client.personal?.city || client.city, client.personal?.state || client.state].filter(Boolean).join(', ') || 'Not set'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-sm text-slate-900">
                          {formatDate(client.personal?.birthdate || client.birthdate, { fallback: 'Not set' })}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {consultantDashboardImage && (
          <Panel title="Company dashboard image">
            <div className="p-4">
              <img src={consultantDashboardImage} alt="Company dashboard" className="block h-auto w-full rounded-[10px] object-contain" />
            </div>
          </Panel>
        )}
      </div>
    )
  }

  const goal = dashboardData?.goal || {}
  const exercise = dashboardData?.exercise || {}
  const nutrition = dashboardData?.nutrition || {}
  const weights = dashboardData?.weights || []
  const latestWeight = weights[0] || {}
  const currentWeight = latestWeight.weight || goal.start_weight || 0

  const packageWeeks = Number(goal.packagepurchased) || 0
  const weeksRemaining = Number(goal.weekstogoal) || 0
  const weeksElapsedPct = packageWeeks > 0 ? Math.round(Math.max(0, Math.min(100, ((packageWeeks - weeksRemaining) / packageWeeks) * 100))) : 0

  const exerciseMetrics = [
    { label: 'Sets', key: 'sets' },
    { label: 'Reps', key: 'reps' },
    { label: 'Exercises', key: 'exercises' },
    { label: 'Cardio', key: 'cardio' },
  ].map(({ label, key }) => {
    const consumed = exercise[key]?.consumed || 0
    const total = exercise[key]?.total || 0
    return { label, consumed, total, progress: pct(consumed, total) }
  })

  const nutritionMetrics = [
    { label: 'Protein', key: 'protein' },
    { label: 'Carbs', key: 'carbs' },
    { label: 'Fat', key: 'fat' },
  ].map(({ label, key }) => {
    const consumed = Math.round(nutrition[key]?.consumed || 0)
    const total = Math.round(nutrition[key]?.total || 0)
    return { label, consumed, total, progress: pct(consumed, total) }
  })

  const caloriesConsumed = Math.round(nutrition.calories?.consumed || 0)
  const caloriesTotal = Math.round(nutrition.calories?.total || 0)

  const todayRows = [
    {
      label: 'Calories',
      helper: null,
      value: caloriesConsumed.toLocaleString(),
      unit: `/ ${caloriesTotal.toLocaleString()} kcal`,
      progress: pct(nutrition.calories?.consumed || 0, nutrition.calories?.total || 0),
    },
    {
      label: 'Sets done',
      helper: null,
      value: exercise.sets?.consumed || 0,
      unit: `/ ${exercise.sets?.total || 0} sets`,
      progress: pct(exercise.sets?.consumed || 0, exercise.sets?.total || 0),
    },
    {
      label: 'Current weight',
      helper: goal.weight_lost_pounds
        ? `${Math.abs(goal.weight_lost_pounds)} lbs ${goal.weight_lost_pounds > 0 ? 'lost' : 'gained'} · goal ${goal.goal_weight || 0} lbs`
        : `Goal ${goal.goal_weight || 0} lbs`,
      value: currentWeight,
      unit: 'lbs',
      progress: goal.goal_weight && currentWeight ? Math.max(0, Math.min(100, (currentWeight / goal.goal_weight) * 100)) : 0,
    },
  ]

  const companyTemplatePackages = [
    { label: 'Food Templates', purchased: !!companyDetails?.food_templates_purchased },
    { label: 'Exercise Templates', purchased: !!companyDetails?.exercise_templates_purchased },
    { label: 'Meal Templates', purchased: !!companyDetails?.meal_templates_purchased },
    { label: 'Recipes', purchased: !!companyDetails?.recipes_purchased },
    { label: 'Exercise Plan Templates', purchased: !!companyDetails?.exercise_plan_templates_purchased },
    { label: 'Nutrition Program Templates', purchased: !!companyDetails?.nutrition_program_templates_purchased },
  ]

  const subtitle = [
    goal.name,
    weeksRemaining > 0 ? `${weeksRemaining} weeks to goal` : null,
    packageWeeks > 0 ? `${packageWeeks} week program` : null,
  ].filter(Boolean).join(' · ')

  return (
    <div className="dashboard-content space-y-5">
      <PageTitle title={`Welcome back${firstName ? `, ${firstName}` : ''}`} subtitle={subtitle || undefined}>
        <DatePicker value={selectedDate} onChange={setSelectedDate} placeholder="Select date" className="h-9 w-[168px] justify-center" />
        <Button onClick={handleRefresh} variant="outline" size="sm" disabled={isLoading} aria-label="Refresh dashboard">
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </PageTitle>

      {isLoading && !dashboardData ? (
        <div className="flex items-center justify-center gap-3 py-16 text-slate-500">
          <RefreshCw className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading your dashboard…</span>
        </div>
      ) : error && !dashboardData ? (
        <div className="flex items-center justify-center py-12">
          <div className="max-w-md rounded-[14px] border border-red-200 bg-red-50 p-6 text-center">
            <h3 className="mb-1 font-semibold text-red-800">Unable to load dashboard</h3>
            <p className="mb-4 text-sm text-red-700">{error}</p>
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className="mr-2 h-4 w-4" />
              Try again
            </Button>
          </div>
        </div>
      ) : (
        <>
          {goal.name && (
            <Panel title={`Program — ${goal.name}`} action={<Link to="/clientprogramarchive" className={linkClass}>View program archive</Link>}>
              <div className="space-y-4 p-4">
                {packageWeeks > 0 && weeksRemaining > 0 && (
                  <div>
                    <div className="mb-2 flex items-baseline justify-between text-sm text-slate-500">
                      <span>{weeksRemaining} of {packageWeeks} weeks remaining</span>
                      <span>{weeksElapsedPct}%</span>
                    </div>
                    <Progress value={weeksElapsedPct} aria-label="Program progress" />
                  </div>
                )}
                <dl className="grid grid-cols-2 gap-y-4 border-t border-slate-100 pt-4 md:grid-cols-4">
                  {[
                    ['Start weight', stat(goal.start_weight, ' lbs')],
                    ['Goal weight', stat(goal.goal_weight, ' lbs')],
                    ['Start body fat', stat(goal.start_bodyfat, '%')],
                    ['Goal body fat', stat(goal.goal_bodyfat, '%')],
                  ].map(([label, value]) => (
                    <div key={label} className="border-l border-slate-200 pl-4">
                      <dt className="text-xs text-slate-500">{label}</dt>
                      <dd className="mt-0.5 text-xl font-semibold tracking-[-0.01em] text-slate-900">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Panel>
          )}

          {isSuperAdmin && (
            <Panel title={companyDetails?.name || 'Company info'}>
              <div className="space-y-5 p-4">
                {companyDetails ? (
                  <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      {[
                        ['Company URL', companyDetails.company_url || '—'],
                        ['Account status', companyDetails.is_trial ? 'Trial' : 'Active'],
                        ['Trial limits', companyDetails.is_trial ? `${companyDetails.trial_limit_days ?? 0}d / ${companyDetails.trial_client_limit ?? 0} clients` : 'No limit'],
                      ].map(([label, value]) => (
                        <div key={label} className="app-stat p-4">
                          <p className="text-xs text-slate-500">{label}</p>
                          <p className="mt-0.5 text-lg font-semibold text-slate-900">{value}</p>
                        </div>
                      ))}
                    </div>

                    <div>
                      <p className="app-kicker mb-2">Purchased templates</p>
                      <div className="flex flex-wrap gap-2">
                        {companyTemplatePackages.map(({ label, purchased }) => (
                          <span
                            key={label}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                              purchased ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            {purchased ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="py-6 text-center text-slate-500">
                    <p className="text-sm font-medium text-slate-700">No company selected</p>
                    <p className="mt-1 text-xs">Select a company to view its details here.</p>
                  </div>
                )}
              </div>
            </Panel>
          )}

          {!isSuperAdmin && (
            <>
              <Panel title="Today" action={<span className="text-[13px] text-slate-500">{selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>}>
                <ul>
                  {todayRows.map(({ label, helper, value, unit, progress }) => (
                    <li key={label} className="grid grid-cols-1 items-center gap-x-6 gap-y-2 border-b border-slate-100 px-4 py-3.5 last:border-b-0 md:grid-cols-[1fr_1fr_200px]">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{label}</p>
                        {helper && <p className="mt-0.5 text-xs text-slate-500">{helper}</p>}
                      </div>
                      <p className="whitespace-nowrap text-slate-900">
                        <span className="text-xl font-semibold tracking-[-0.01em]">{value}</span>
                        <span className="ml-1.5 text-sm text-slate-500">{unit}</span>
                      </p>
                      <Progress value={progress} aria-label={`${label} progress`} />
                    </li>
                  ))}
                </ul>
              </Panel>

              <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
                <Panel title="Exercise" action={<Link to="/clientexercise" className={linkClass}>Open today&apos;s workout</Link>}>
                  <ul>
                    {exerciseMetrics.map(({ label, consumed, total, progress }) => (
                      <li key={label} className="flex items-center justify-between gap-4 border-b border-slate-100 px-4 py-3 last:border-b-0">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{label}</p>
                          <p className="mt-0.5 text-xs text-slate-500">{consumed} / {total} · {Math.round(progress)}%</p>
                        </div>
                        <Progress value={progress} className="w-[130px]" aria-label={`${label} progress`} />
                      </li>
                    ))}
                  </ul>
                  {exerciseMetrics.every((metric) => metric.progress === 0) && (
                    <p className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">No workouts logged yet — start your first workout to track progress.</p>
                  )}
                </Panel>

                <Panel title="Nutrition" action={<Link to="/clientnutrition" className={linkClass}>Log a meal</Link>}>
                  <ul>
                    {nutritionMetrics.map(({ label, consumed, total, progress }) => (
                      <li key={label} className="border-b border-slate-100 px-4 py-3 last:border-b-0">
                        <div className="mb-2 flex items-baseline justify-between">
                          <p className="text-sm font-semibold text-slate-900">{label}</p>
                          <p className="text-xs text-slate-500">{consumed}g / {total}g</p>
                        </div>
                        <Progress value={progress} aria-label={`${label} progress`} />
                      </li>
                    ))}
                  </ul>
                  {caloriesConsumed === 0 && (
                    <p className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">No meals logged yet today.</p>
                  )}
                </Panel>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default Dashboard
